import { useState } from "react";

import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ENRICHMENT_RESOURCE,
  hitlQueueReturnTo,
} from "@/pages/tina/items/components/panels/hitl-panel/queue-return";
import type { ResolutionComponentProps } from "@/pages/tina/items/components/panels/hitl-panel/resolution-components";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import type {
  CuratorReviewFieldHold,
  EnrichmentCuratorReviewProblem,
  HitlContext,
} from "@/types/tina";

type FieldDecision = "accept" | "decline";

/**
 * The one human label per curated field name — the exact fifteen names the
 * artist-field-locks spec fixes (five top-level, ten under `enrichment`). A
 * field this build predates still renders, through {@link humanizeFallback}.
 */
const CURATED_FIELD_LABEL: Record<string, string> = {
  name: "Name",
  about: "About",
  genres: "Genres",
  socialLinks: "Social links",
  media: "Media",
  "enrichment.about": "About (enrichment)",
  "enrichment.shortDescription": "Short description (enrichment)",
  "enrichment.seoTitle": "SEO title (enrichment)",
  "enrichment.faq": "FAQ (enrichment)",
  "enrichment.keywords": "Keywords (enrichment)",
  "enrichment.relatedSearches": "Related searches (enrichment)",
  "enrichment.genres": "Genres (enrichment)",
  "enrichment.features": "Features (enrichment)",
  "enrichment.isGroup": "Is group (enrichment)",
  "enrichment.members": "Members (enrichment)",
};

/** The two curated fields whose value is a genre list — rendered as name chips, not JSON. */
const GENRE_FIELDS = new Set(["genres", "enrichment.genres"]);

/** camelCase → "Camel case", with the `enrichment.` prefix moved to a trailing "(enrichment)". */
function humanizeFallback(field: string): string {
  const isEnrichment = field.startsWith("enrichment.");
  const bare = isEnrichment ? field.slice("enrichment.".length) : field;
  const words = bare
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase();
  const label = words.charAt(0).toUpperCase() + words.slice(1);
  return isEnrichment ? `${label} (enrichment)` : label;
}

/** Exported for the review queue's field-count cell and its own tests. */
export function curatedFieldLabel(field: string): string {
  return CURATED_FIELD_LABEL[field] ?? humanizeFallback(field);
}

/**
 * `declinedAt`/`declined_at` — Tina's field-hold entries carry either spelling (see
 * `CuratorReviewFieldHold`'s doc comment). Read through this rather than the raw key.
 */
function curatorReviewDeclinedAt(entry: CuratorReviewFieldHold): string | null {
  return entry.declinedAt ?? entry.declined_at ?? null;
}

/** The `enrichment_curator_review` problem, or null for every other reason. */
export function curatorReviewProblemOf(
  context: HitlContext | null | undefined,
): EnrichmentCuratorReviewProblem | null {
  const problem = context?.problem;
  if (!problem || problem.reason !== "enrichment_curator_review") return null;
  return problem;
}

function genreChipLabel(value: unknown): string {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "name" in value) {
    const name = (value as { name?: unknown }).name;
    if (typeof name === "string") return name;
  }
  return JSON.stringify(value);
}

/**
 * One curated value, current or proposed — strings as text, booleans as "Yes"/"No",
 * `genres`/`enrichment.genres` as name chips, every other non-empty list or object
 * pretty-printed JSON, and null/undefined, an empty list or "" as "—" (owner ruling:
 * the review focus in the artist-field-locks brief).
 */
function CuratedValue({ field, value }: { field: string; value: unknown }) {
  if (value === null || value === undefined) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  if (typeof value === "boolean") {
    return <span className="text-sm">{value ? "Yes" : "No"}</span>;
  }
  // Before the genre-chip branch: an empty list is "—" whichever field it is on,
  // never an empty row of chips or a bare "[]".
  if (Array.isArray(value) && value.length === 0) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  if (GENRE_FIELDS.has(field) && Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((entry, index) => (
          <Badge key={index} variant="outline" className="font-normal">
            {genreChipLabel(entry)}
          </Badge>
        ))}
      </div>
    );
  }
  if (typeof value === "string") {
    return <span className="whitespace-pre-wrap text-sm">{value || "—"}</span>;
  }
  return (
    <pre className="overflow-x-auto rounded-md border bg-muted/40 p-2 font-mono text-[11px] leading-relaxed">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

/**
 * The curator-review panel for `enrichment_curator_review` — an automatic enrichment
 * write held back one or more curated fields on an artist because they disagreed with
 * what a curator already put there (artist-field-locks, 2026-09-28).
 *
 * One row per held field, each independently accepted (write the automatic value, clear
 * the hold) or declined (keep the curator's value, stamp a fresh `declinedAt`). Modelled
 * on `refresh-resolution.tsx`'s per-row Accept/Reject, keyed by `field` rather than index —
 * there is exactly one decision shape here, so this panel carries none of that file's
 * per-`kind` renderers.
 */
export function CuratorReviewResolution({
  item,
  hitlContext,
  onResolved,
}: ResolutionComponentProps) {
  const { resolveHitl } = useItemsRepository();
  const problem = curatorReviewProblemOf(hitlContext);
  const [decisions, setDecisions] = useState<Record<string, FieldDecision>>({});
  const [submitting, setSubmitting] = useState(false);

  // The registry only ever mounts this component for `hitl_reason ===
  // "enrichment_curator_review"`, so this is a defensive fallback for a
  // mismatched context rather than a path a curator can actually hit.
  if (!problem) {
    return (
      <p className="text-sm text-muted-foreground">
        This HITL context is not a curator review — nothing to resolve here.
      </p>
    );
  }

  const fields = problem.fields;
  const canSubmit =
    fields.length > 0 && fields.every((entry) => decisions[entry.field]);

  function setDecision(field: string, decision: FieldDecision) {
    setDecisions((prev) => ({ ...prev, [field]: decision }));
  }

  function setAll(decision: FieldDecision) {
    setDecisions(
      Object.fromEntries(fields.map((entry) => [entry.field, decision])),
    );
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await resolveHitl(
        item.id,
        {
          resolution: {
            kind: "enrichment.curator_review",
            decisions,
          },
        },
        // Never the shared HITL queue — a curator-review park was seeded on its own
        // `enrichment_artist_curation` job and lives on `/tina/enrichment`, the same
        // surface `enrichment_low_confidence` uses. `hitlQueueReturnTo` already
        // knows that mapping; the literal is only the fallback for a context this
        // reader somehow does not recognise.
        { navigateTo: hitlQueueReturnTo(hitlContext) ?? ENRICHMENT_RESOURCE },
      );
      onResolved();
    } catch {
      // `resolveHitl` has already toasted the failure and captured it to
      // Sentry, then rethrown so a caller that wants to stay put can tell.
      // This one has nothing left to do: `finally` re-enables the buttons and
      // the curator can try again. Letting it escape the `void handleSubmit()`
      // call site bought an unhandled rejection and a second, identical
      // Sentry event via the global handler — never a better error message.
    } finally {
      setSubmitting(false);
    }
  }

  if (fields.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        This park carries no held fields to decide.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
          Held fields
          {problem.artist_name ? ` — ${problem.artist_name}` : ""}
        </h3>
        <div className="flex gap-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setAll("accept")}
          >
            Accept all
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setAll("decline")}
          >
            Decline all
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        An automatic enrichment write proposed a new value for a field this
        artist has curated. Accept writes the new value and clears the hold;
        Decline keeps the curated value — Tina won&rsquo;t ask again about it
        for about six months.
      </p>

      <div className="flex flex-col gap-3">
        {fields.map((entry) => (
          <FieldRow
            key={entry.field}
            entry={entry}
            decision={decisions[entry.field]}
            onChange={(decision) => setDecision(entry.field, decision)}
          />
        ))}
      </div>

      <Button
        type="button"
        disabled={!canSubmit || submitting}
        onClick={() => void handleSubmit()}
      >
        {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
        Submit
      </Button>
    </div>
  );
}

function FieldRow({
  entry,
  decision,
  onChange,
}: {
  entry: CuratorReviewFieldHold;
  decision: FieldDecision | undefined;
  onChange: (decision: FieldDecision) => void;
}) {
  const declinedAt = curatorReviewDeclinedAt(entry);
  return (
    <div className="flex flex-col gap-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-foreground">
          {curatedFieldLabel(entry.field)}
        </span>
        {declinedAt && (
          <span className="text-xs text-muted-foreground">
            declined on {new Date(declinedAt).toLocaleDateString()}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <span className="text-[11px] uppercase text-muted-foreground">
            Current (curator&rsquo;s)
          </span>
          <CuratedValue field={entry.field} value={entry.current} />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[11px] uppercase text-muted-foreground">
            Proposed (automatic)
          </span>
          <CuratedValue field={entry.field} value={entry.proposed} />
        </div>
      </div>
      <div className="flex gap-1">
        <Button
          type="button"
          size="sm"
          variant={decision === "accept" ? "default" : "outline"}
          onClick={() => onChange("accept")}
        >
          Accept
        </Button>
        <Button
          type="button"
          size="sm"
          variant={decision === "decline" ? "destructive" : "outline"}
          onClick={() => onChange("decline")}
        >
          Decline
        </Button>
      </div>
    </div>
  );
}
